import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Code, ConnectError } from '@connectrpc/connect';
import { ProposalSubmissionApiTypes } from '@mono/proto-clients';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraHeading, SpecraInput, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { z } from 'zod';
import { proposalSubmissionClient } from '../../../lib/connect/clients';
import { draftKey, emptyDraft, persistDraft, restoreDraft, validSubmissionTime } from './submissionDraft';
import { loadSubmissionSources } from './submissionSources';
const ids = Testids.proposalSubmission;
const message = (error)=>error instanceof Error ? error.message : 'Submission could not be loaded or recorded.';
const snapshotPinsSchema = z.object({
    files: z.array(z.object({
        artifact_id: z.string(),
        artifact_version: z.number()
    })).catch([]),
    sections: z.array(z.object({
        version_id: z.string()
    })).catch([])
});
function snapshotPins(json) {
    try {
        return snapshotPinsSchema.parse(JSON.parse(json));
    } catch  {
        return {
            files: [],
            sections: []
        };
    }
}
export function ProposalSubmissionPanel({ projectId, owner, account, tenantId }) {
    const key = draftKey(account, projectId);
    const [initial] = useState(()=>{
        try {
            return {
                ...restoreDraft(key, projectId),
                error: ''
            };
        } catch (error) {
            return {
                draft: emptyDraft,
                pending: null,
                error: `Draft recovery failed: ${message(error)}`
            };
        }
    });
    const [draft, setDraft] = useState(initial.draft);
    const [pending, setPending] = useState(initial.pending);
    const [error, setError] = useState(initial.error);
    const [busy, setBusy] = useState(false);
    const [rejected, setRejected] = useState(false);
    const [notice, setNotice] = useState('');
    const [recorded, setRecorded] = useState();
    const lock = useRef(false);
    const history = useQuery({
        queryKey: [
            'proposal-submissions',
            account,
            projectId
        ],
        queryFn: async ()=>(await proposalSubmissionClient.listProposalSubmissions({
                projectId
            })).submissions,
        retry: false,
        gcTime: 0
    });
    const sources = useQuery({
        queryKey: [
            'proposal-submission-sources',
            account,
            projectId
        ],
        queryFn: ()=>loadSubmissionSources(projectId, tenantId),
        enabled: owner,
        retry: false,
        gcTime: 0
    });
    const data = !sources.isError && !sources.isFetching ? sources.data : undefined;
    const rows = !history.isError ? history.data ?? [] : [];
    const leaf = rows.find((row)=>!rows.some((next)=>next.supersedesId === row.id));
    const frozen = busy || !!pending;
    function update(next) {
        if (lock.current || pending) return;
        setDraft(next);
        try {
            persistDraft(key, next, null);
            setError('');
        } catch (error) {
            setError(`Draft could not be saved: ${message(error)}`);
        }
    }
    const valid = owner && !!data && !history.isError && !history.isFetching && (!leaf || leaf.id === draft.supersedesId) && data.scenarios.some((v)=>v.versionId === draft.pricingVersionId) && data.scenarios.some((v)=>v.versionId === draft.staffingVersionId) && data.solicitations.some((v)=>v.id === draft.solicitationVersionId) && draft.files.every((pin)=>data.files.some((f)=>f.artifactId === pin.artifactId && f.version === pin.artifactVersion)) && draft.sectionVersionIds.every((id)=>data.sections.some((s)=>s.id === id)) && draft.files.length + draft.sectionVersionIds.length > 0 && [
        draft.submittedBy,
        draft.method,
        draft.recipient
    ].every((s)=>!!s.trim()) && validSubmissionTime(draft.submittedAt, draft.submittedTimezone);
    // WRT-30: freeze a volume document version into a file artifact, then pin it.
    const [freezing, setFreezing] = useState('');
    async function freeze(documentId, versionId) {
        if (lock.current || pending || freezing) return;
        setFreezing(versionId);
        setError('');
        try {
            const res = await proposalSubmissionClient.freezeDocumentVersionForSubmission({
                projectId,
                documentId,
                versionId
            });
            await sources.refetch();
            if (!draft.files.some((p)=>p.artifactId === res.artifactId)) {
                update({
                    ...draft,
                    files: [
                        ...draft.files,
                        {
                            artifactId: res.artifactId,
                            artifactVersion: res.artifactVersion
                        }
                    ]
                });
            }
            setNotice(`Froze ${res.filename} for this submission.`);
        } catch (err) {
            setError(message(err));
        } finally{
            setFreezing('');
        }
    }
    async function record() {
        if (lock.current || !owner || !pending && !valid) return;
        lock.current = true;
        setBusy(true);
        setError('');
        setNotice('');
        setRejected(false);
        try {
            const request = pending ? ProposalSubmissionApiTypes.RecordProposalSubmissionRequest.fromJsonString(pending) : new ProposalSubmissionApiTypes.RecordProposalSubmissionRequest({
                projectId,
                submission: {
                    ...draft,
                    submittedAt: Timestamp.fromDate(new Date(draft.submittedAt)),
                    receiptReference: draft.receiptReference.trim() || undefined,
                    requestId: crypto.randomUUID()
                }
            });
            const serialized = request.toJsonString();
            // Persist BEFORE the RPC. Failure to persist must never send a nonrecoverable request.
            persistDraft(key, draft, serialized);
            setPending(serialized);
            const result = await proposalSubmissionClient.recordProposalSubmission(request);
            if (!result.submission?.id) throw new Error('No recorded submission returned. Retry the exact request to recover.');
            // Keep the authoritative response even if the following history refresh fails.
            setRecorded(result.submission);
            persistDraft(key, emptyDraft, null);
            setPending(null);
            setDraft(emptyDraft);
            setNotice(`Recorded submission ${result.submission.id}. Nothing was sent externally.`);
            await history.refetch();
        } catch (error) {
            setError(message(error));
            setRejected(error instanceof ConnectError && [
                Code.InvalidArgument,
                Code.PermissionDenied,
                Code.Unauthenticated,
                Code.NotFound,
                Code.FailedPrecondition,
                Code.AlreadyExists
            ].includes(error.code));
        } finally{
            lock.current = false;
            setBusy(false);
        }
    }
    // A returned snapshot is authoritative; never rebuild it from producer heads.
    const displayed = history.isError ? [] : recorded && !rows.some((row)=>row.id === recorded.id) ? [
        ...rows,
        recorded
    ] : rows;
    const hasSuccessor = (id)=>displayed.some((row)=>row.supersedesId === id);
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": ids.panel,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    size: "sm",
                    children: "Actual proposal submissions"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Record what was actually submitted. Nothing is sent externally. History and its frozen package remain immutable; corrections append a revision."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": ids.refresh,
                    variant: "outline",
                    disabled: busy || history.isFetching || owner && sources.isFetching,
                    onClick: ()=>{
                        void history.refetch();
                        if (owner) void sources.refetch();
                    },
                    children: "Refresh submissions and sources"
                }),
                (history.isFetching || owner && sources.isFetching) && /*#__PURE__*/ _jsx("output", {
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        children: "Loading submissions and permitted sources…"
                    })
                }),
                (error || history.error || owner && sources.error) && /*#__PURE__*/ _jsx(SpecraText, {
                    "data-testid": ids.error,
                    role: "alert",
                    children: error || message(history.error ?? sources.error)
                }),
                notice && /*#__PURE__*/ _jsx("output", {
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        children: notice
                    })
                }),
                !history.isFetching && !history.isError && !displayed.length && /*#__PURE__*/ _jsx(SpecraText, {
                    children: "No actual submissions recorded."
                }),
                displayed.map((row)=>/*#__PURE__*/ _jsxs("section", {
                        "data-testid": ids.entry,
                        "data-submission-id": row.id,
                        "data-supersedes-id": row.supersedesId,
                        className: "space-y-4 rounded-lg border border-border p-4",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                size: "sm",
                                children: row.supersedesId ? 'Revised submission' : 'Original submission'
                            }),
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                "data-testid": ids.status,
                                variant: row.receiptReference ? 'success' : 'warning',
                                children: row.receiptReference ? `Receipt: ${row.receiptReference}` : 'Unconfirmed'
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2 break-words",
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Actual submission time: ",
                                            row.submittedAt?.toDate().toISOString()
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Submission timezone: ",
                                            row.submittedTimezone
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Actual submitted by: ",
                                            row.submittedBy
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Submission method: ",
                                            row.method
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Recipient: ",
                                            row.recipient
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Recorded at: ",
                                            row.recordedAt?.toDate().toISOString()
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Recorded by: ",
                                            row.recordedBy
                                        ]
                                    }),
                                    row.notes && /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block whitespace-pre-wrap",
                                        children: [
                                            "Notes: ",
                                            row.notes
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("details", {
                                className: "space-y-2 break-words",
                                children: [
                                    /*#__PURE__*/ _jsx("summary", {
                                        children: "Frozen package and version provenance"
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Submission ",
                                            row.id
                                        ]
                                    }),
                                    row.supersedesId && /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        "data-testid": ids.predecessor,
                                        children: [
                                            "Supersedes ",
                                            row.supersedesId
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Pricing version: ",
                                            row.pricingVersionId
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Staffing version: ",
                                            row.staffingVersionId
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Solicitation version: ",
                                            row.solicitationVersionId
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        "data-testid": ids.snapshot,
                                        "data-pricing-version-id": row.pricingVersionId,
                                        "data-staffing-version-id": row.staffingVersionId,
                                        "data-solicitation-version-id": row.solicitationVersionId,
                                        "data-artifact-pins": JSON.stringify(snapshotPins(row.snapshotJson).files),
                                        "data-section-version-ids": JSON.stringify(snapshotPins(row.snapshotJson).sections.map((s)=>s.version_id)),
                                        className: "block whitespace-pre-wrap break-words",
                                        children: row.snapshotJson || 'Snapshot unavailable. Refresh history.'
                                    })
                                ]
                            }),
                            owner && !hasSuccessor(row.id) && /*#__PURE__*/ _jsxs(SpecraButton, {
                                "data-testid": ids.revise,
                                disabled: frozen,
                                onClick: ()=>update({
                                        ...emptyDraft,
                                        supersedesId: row.id
                                    }),
                                children: [
                                    "Revise submission ",
                                    row.id
                                ]
                            })
                        ]
                    }, row.id)),
                owner && /*#__PURE__*/ _jsxs("section", {
                    className: "space-y-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            size: "sm",
                            children: draft.supersedesId ? `Revision of ${draft.supersedesId}` : 'Record actual submission'
                        }),
                        data && (!data.scenarios.length || !data.solicitations.length || !data.files.length && !data.sections.length) && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Submission sources are incomplete. Freeze pricing and staffing scenario versions in Pricing, establish a solicitation version in Amendment review, and upload final files or create locked authoring review/milestone versions. Then refresh."
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Choose pricing and staffing independently from frozen scenario versions with saved pricing and staffing. No current head is selected automatically."
                        }),
                        [
                            'pricing',
                            'staffing'
                        ].map((kind)=>/*#__PURE__*/ _jsxs("section", {
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        children: [
                                            kind === 'pricing' ? 'Pricing' : 'Staffing',
                                            " scenario version"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        "aria-label": kind === 'pricing' ? 'Pricing scenario version' : 'Staffing scenario version',
                                        "data-testid": ids[kind],
                                        value: draft[`${kind}VersionId`],
                                        disabled: frozen || !data,
                                        onChange: (e)=>update({
                                                ...draft,
                                                [`${kind}VersionId`]: e.target.value
                                            }),
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Choose a frozen version"
                                            }),
                                            data?.scenarios.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                                    value: v.versionId,
                                                    "data-version-id": v.versionId,
                                                    "data-scenario-id": v.scenarioId,
                                                    children: [
                                                        v.scenarioName,
                                                        " · v",
                                                        v.versionNo,
                                                        " · ",
                                                        v.versionId
                                                    ]
                                                }, v.versionId))
                                        ]
                                    })
                                ]
                            }, kind)),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Solicitation version"
                        }),
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Solicitation version",
                            "data-testid": ids.solicitation,
                            value: draft.solicitationVersionId,
                            disabled: frozen || !data,
                            onChange: (e)=>update({
                                    ...draft,
                                    solicitationVersionId: e.target.value
                                }),
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Choose a solicitation version"
                                }),
                                data?.solicitations.map((v)=>/*#__PURE__*/ _jsxs("option", {
                                        value: v.id,
                                        "data-version-id": v.id,
                                        children: [
                                            v.sourceName,
                                            " · v",
                                            v.versionNumber,
                                            " · ",
                                            v.id
                                        ]
                                    }, v.id))
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            size: "sm",
                            children: "Final files and locked authoring versions"
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Choose at least one file or locked section version. Files pin exact artifact ID and version. Authoring choices show up to 100 recent versions per current user section."
                        }),
                        data && !data.files.length && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "No eligible uploaded files."
                        }),
                        data?.files.map((f)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "outline",
                                "data-testid": ids.file,
                                "data-artifact-id": f.artifactId,
                                "data-artifact-version": f.version,
                                "aria-pressed": draft.files.some((p)=>p.artifactId === f.artifactId && p.artifactVersion === f.version),
                                disabled: frozen,
                                onClick: ()=>update({
                                        ...draft,
                                        files: draft.files.some((p)=>p.artifactId === f.artifactId) ? draft.files.filter((p)=>p.artifactId !== f.artifactId) : [
                                            ...draft.files,
                                            {
                                                artifactId: f.artifactId,
                                                artifactVersion: f.version
                                            }
                                        ]
                                    }),
                                children: [
                                    f.filename,
                                    " · v",
                                    f.version,
                                    " · ",
                                    f.artifactId
                                ]
                            }, `${f.artifactId}:${f.version}`)),
                        data && !data.sections.length && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "No locked review or milestone section versions."
                        }),
                        data?.sections.map((s)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "outline",
                                "data-testid": ids.section,
                                "data-section-id": s.sectionId,
                                "data-version-id": s.id,
                                "data-source-version-id": s.sourceVersionId,
                                "aria-pressed": draft.sectionVersionIds.includes(s.id),
                                disabled: frozen,
                                onClick: ()=>update({
                                        ...draft,
                                        sectionVersionIds: draft.sectionVersionIds.includes(s.id) ? draft.sectionVersionIds.filter((id)=>id !== s.id) : [
                                            ...draft.sectionVersionIds,
                                            s.id
                                        ]
                                    }),
                                children: [
                                    s.title,
                                    " · ",
                                    s.kind,
                                    " · v",
                                    s.version,
                                    " · ",
                                    s.id
                                ]
                            }, s.id)),
                        (data?.volumeDocuments ?? []).length > 0 && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Volume documents: freezing one exports its locked version without comments into a project file, marks the version Submitted and adds the file to this submission."
                        }),
                        (data?.volumeDocuments ?? []).map((d)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "outline",
                                "data-testid": ids.volumeDocument,
                                "data-document-id": d.documentId,
                                "data-version-id": d.versionId,
                                disabled: frozen || freezing !== '',
                                isLoading: freezing === d.versionId,
                                onClick: ()=>void freeze(d.documentId, d.versionId),
                                children: [
                                    d.volumeName,
                                    " · ",
                                    d.label,
                                    " · v",
                                    d.versionNumber,
                                    d.versionLabel ? ` · ${d.versionLabel}` : '',
                                    " · Freeze for submission"
                                ]
                            }, `${d.documentId}:${d.versionId}`)),
                        [
                            [
                                'submittedAt',
                                'Actual submission time (ISO 8601 with offset)'
                            ],
                            [
                                'submittedTimezone',
                                'Submission timezone'
                            ],
                            [
                                'submittedBy',
                                'Actual submitted by'
                            ],
                            [
                                'method',
                                'Submission method'
                            ],
                            [
                                'recipient',
                                'Recipient'
                            ],
                            [
                                'receiptReference',
                                'Receipt reference (optional)'
                            ],
                            [
                                'notes',
                                'Notes (optional)'
                            ]
                        ].map(([field, label])=>/*#__PURE__*/ _jsxs("section", {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        children: label
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": label,
                                        "data-testid": ids[field],
                                        value: draft[field],
                                        disabled: frozen,
                                        onChange: (e)=>update({
                                                ...draft,
                                                [field]: e.target.value
                                            })
                                    })
                                ]
                            }, field)),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Use an explicit time with offset, for example 2026-10-20T12:00:00-07:00, and an IANA timezone such as America/Phoenix. Leave an unknown receipt blank: it remains Unconfirmed."
                        }),
                        pending ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    children: "There is an unresolved request. Retry its exact saved payload and request ID before recording another submission."
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": ids.retry,
                                    isLoading: busy,
                                    disabled: busy,
                                    onClick: ()=>void record(),
                                    children: "Retry exact submission"
                                }),
                                rejected && /*#__PURE__*/ _jsx(SpecraButton, {
                                    disabled: busy,
                                    onClick: ()=>{
                                        try {
                                            persistDraft(key, draft, null);
                                            setPending(null);
                                            setRejected(false);
                                        } catch (error) {
                                            setError(message(error));
                                        }
                                    },
                                    children: "Edit rejected submission"
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.record,
                            isLoading: busy,
                            disabled: !valid || busy,
                            onClick: ()=>void record(),
                            children: "Record actual submission"
                        }),
                        busy && /*#__PURE__*/ _jsx("output", {
                            children: /*#__PURE__*/ _jsx(SpecraText, {
                                children: "Recording actual submission… Please wait."
                            })
                        })
                    ]
                })
            ]
        })
    });
}
